import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { LogoMark } from "./components/Logo";
import { GuaranteeBadge, StickyCTABar, BonusStack, CertificateMockup, PaymentMethods } from "./components/ConversionKit";

const BG = "#FAF7EF";
const BG_ALT = "#FFFFFF";
const BG_ELEV = "#FFFFFF";
const CREAM = "#141414";
const GOLD = "#3B5EDF";
const BLUE = "#3B5EDF";
const LIME = "#D9F154";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  en: {
    promo: "THE CAPITAL PLAYBOOK · COMING SOON ON HOTMART",
    langBtn: "ES",
    tag: "— THE FLAGSHIP COURSE · SELF-PACED · BILINGUAL",
    titleA: "The Capital",
    titleHL: "Playbook.",
    titleB: "How to raise institutional debt without dilution.",
    sub: "The 8-module operator's method. 52 video lessons. 30+ templates. 5 real term sheets torn apart. All shipped by the guy who signed $1.2B+ of them.",
    priceLabel: "One-time",
    price: "$497",
    priceSub: "or 3 × $187 · MXN ≈ $8,900",
    priceGuarantee: "14-day money-back · No questions asked",
    ctaWaitlist: "Notify me when it opens →",
    ctaSecondary: "Get Module 1 · FREE",
    email: "you@company.com",
    stat1: "modules",
    stat2: "video lessons",
    stat3: "hours of content",
    stat4: "templates",
    outcomesTag: "— WHAT YOU'LL WALK AWAY WITH",
    outcomesTitle: "Four outcomes.",
    outcomesTitleHL: "One playbook.",
    outcomes: [
      { n: "01", t: "A capital stack that fits your business", d: "Diagnose your cap table, cost of capital and readiness in the first two modules. Ship a memo you can hand to a board." },
      { n: "02", t: "Three term-sheet templates that actually work", d: "The exact language I use to negotiate warehouse, bridge and mezzanine facilities. Copy, adapt, deploy." },
      { n: "03", t: "Access to 60+ curated lenders", d: "Directory of the credit funds, family offices and NBFIs I know personally. Filtered by asset class and ticket." },
      { n: "04", t: "Certified as a Capital Operator", d: "Ship the final case study, earn a digital badge you can share on LinkedIn. Trust signal for your next raise." },
    ],
    modulesTag: "— THE 8-MODULE CURRICULUM",
    modulesTitle: "Eight modules.",
    modulesTitleHL: "Fifty-two lessons.",
    freeLabel: "FREE PREVIEW",
    templatesTag: "— WHAT YOU DOWNLOAD",
    templatesTitle: "Thirty-plus tools.",
    templatesTitleHL: "Zero fluff.",
    templatesSub: "Every template is what I use every week — vetted by 200+ operators, refined over 10 years.",
    instructorTag: "— YOUR INSTRUCTOR",
    instructorTitle: "Ernesto Gómez Berjón",
    instructorRole: "Managing Partner · ERGO Capital · Founder Sivo",
    instructorBio: "Ten years structuring debt in nine jurisdictions. $1.2B+ in signed term sheets. Advisor to Trafalgar, Kapital México, BAASA, Grupo Fagro. Now teaching everything I've learned — same operator, no theory.",
    quote: '"The best debt is the one you don\'t have to explain twice."',
    faqTag: "— QUESTIONS",
    faqTitle: "Read before you buy.",
    finalTag: "— READY?",
    finalTitle: "The course opens soon.",
    finalTitleHL: "Get on the list.",
    finalSub: "You'll get Module 1 immediately + a 20% early-bird discount when Hotmart goes live.",
    footer: "© 2026 · the debt guy · ernesto gómez berjón",
    back: "← back to landing",
    freemium1: "Module 1 on the house.",
    freemium1HL: "45 minutes. No card.",
    freemium1Sub: "Six lessons. The foundations. Enough to change how you look at your cap table forever. Delivered to your inbox instantly.",
    successMsg: "You're in. Check your inbox for Module 1 access.",
  },
  es: {
    promo: "THE CAPITAL PLAYBOOK · PRÓXIMAMENTE EN HOTMART",
    langBtn: "EN",
    tag: "— CURSO FLAGSHIP · A TU RITMO · EN ESPAÑOL",
    titleA: "The Capital",
    titleHL: "Playbook.",
    titleB: "Cómo levantar deuda institucional sin diluirte.",
    sub: "El método de 8 módulos del operador. 52 lecciones en video. 30+ plantillas. 5 term sheets reales desmenuzados. Todo del tipo que firmó $1.2B+ de ellos.",
    priceLabel: "Pago único",
    price: "$199 USD",
    priceSub: "o 3 × $69 · MXN ≈ $3,600",
    priceGuarantee: "14 días money-back · sin preguntas",
    ctaWaitlist: "Avísame cuando abra →",
    ctaSecondary: "Módulo 1 · GRATIS",
    email: "tucorreo@empresa.com",
    stat1: "módulos",
    stat2: "lecciones",
    stat3: "horas de contenido",
    stat4: "plantillas",
    outcomesTag: "— QUÉ TE LLEVAS",
    outcomesTitle: "Cuatro resultados.",
    outcomesTitleHL: "Un playbook.",
    outcomes: [
      { n: "01", t: "Un capital stack que sí funciona para tu negocio", d: "Diagnostica tu cap table, costo de capital y readiness en los dos primeros módulos. Sale un memo que puedes pasar al board." },
      { n: "02", t: "Tres term-sheet templates que sí sirven", d: "El lenguaje exacto que uso para negociar warehouse, bridge y mezzanine. Copia, adapta, despliega." },
      { n: "03", t: "Acceso a 60+ lenders curados", d: "Directorio de credit funds, family offices y NBFIs que conozco personalmente. Filtrable por asset class y ticket." },
      { n: "04", t: "Certificado como Capital Operator", d: "Entrega el case study final, obtén un badge digital para tu LinkedIn. Trust signal para tu próximo raise." },
    ],
    modulesTag: "— EL CURRÍCULO · 8 MÓDULOS",
    modulesTitle: "Ocho módulos.",
    modulesTitleHL: "Cincuenta y dos lecciones.",
    freeLabel: "GRATIS",
    templatesTag: "— QUÉ DESCARGAS",
    templatesTitle: "Treinta y tantas herramientas.",
    templatesTitleHL: "Cero relleno.",
    templatesSub: "Cada plantilla es lo que uso cada semana — validado por 200+ operadores, refinado en 16 años de deals.",
    instructorTag: "— TU INSTRUCTOR",
    instructorTitle: "Ernesto Gómez Berjón",
    instructorRole: "Managing Partner · ERGO Capital · Fundador Sivo",
    instructorBio: "16 años estructurando deuda en nueve jurisdicciones. $1.2B+ en term sheets firmados. Mentor de Endeavor y de compañías de Y Combinator. Advisor de Trafalgar, Kapital, BAASA y Grupo Fagro. Ahora enseñando todo lo aprendido — mismo operador, cero teoría.",
    quote: '"La mejor deuda es la que no tienes que explicar dos veces."',
    faqTag: "— PREGUNTAS",
    faqTitle: "Lee antes de comprar.",
    finalTag: "— ¿LISTO?",
    finalTitle: "El curso abre pronto.",
    finalTitleHL: "Anótate en la lista.",
    finalSub: "Recibes el Módulo 1 al instante + 20% early-bird cuando salga en Hotmart.",
    footer: "© 2026 · the debt guy · ernesto gómez berjón",
    back: "← volver a landing",
    freemium1: "Módulo 1 · va por la casa.",
    freemium1HL: "45 minutos. Sin tarjeta.",
    freemium1Sub: "Seis lecciones. Los fundamentos. Suficiente para cambiar cómo ves tu cap table para siempre. Al instante en tu inbox.",
    successMsg: "Listo. Revisa tu inbox — el Módulo 1 va en camino.",
  },
};

const MODULES = [
  {
    n: "01", t_en: "Foundations", t_es: "Fundamentos",
    len_en: "45 min · 6 lessons", len_es: "45 min · 6 lecciones",
    free: true,
    d_en: "The 5 capital sources, when each wins, and the cost-of-capital equation everyone gets wrong.",
    d_es: "Los 5 tipos de capital, cuándo gana cada uno y la ecuación de costo que todos malinterpretan.",
    lessons_en: ["The 5 capital sources", "When each wins vs loses", "The cost-of-capital equation", "The 3 raise archetypes", "How lenders actually think", "Where you are vs where you need to be"],
    lessons_es: ["Los 5 tipos de capital", "Cuándo gana cada uno", "La ecuación de costo de capital", "Los 3 arquetipos de raise", "Cómo piensa realmente un lender", "Dónde estás vs dónde debes estar"],
  },
  {
    n: "02", t_en: "Diagnose", t_es: "Diagnose",
    len_en: "1.5h · 7 lessons + template", len_es: "1.5h · 7 lecciones + plantilla",
    d_en: "Read your cap table like a lender. Score your readiness. Ship a diagnostic memo.",
    d_es: "Lee tu cap table como te lo lee un lender. Puntúa tu readiness. Sale un memo diagnóstico.",
    lessons_en: ["Read your cap table like a lender", "The waterfall model walkthrough", "Founder dilution math", "Debt vs equity: when each unlocks", "Cost-of-capital calculator", "Lender readiness scorecard", "Homework · score your company"],
    lessons_es: ["Cap table visto por un lender", "Walkthrough del waterfall model", "Math de dilución", "Debt vs equity: cuándo cada uno", "Cost-of-capital calculator", "Lender readiness scorecard", "Homework · puntúa tu empresa"],
  },
  {
    n: "03", t_en: "Structure", t_es: "Structure",
    len_en: "2h · 8 lessons + 3 templates", len_es: "2h · 8 lecciones + 3 plantillas",
    d_en: "Anatomy of a term sheet. The 3 templates I actually use to negotiate warehouse, bridge and mezz.",
    d_es: "Anatomía de un term sheet. Los 3 templates reales para warehouse, bridge y mezz.",
    lessons_en: ["Anatomy of a term sheet", "Covenants that kill you", "Warehouse facility end-to-end", "Bridge / revolving facility", "SAFE-to-debt conversion", "Mezzanine 101", "Ratcheting & MFN clauses", "The 3 templates I actually use"],
    lessons_es: ["Anatomía de un term sheet", "Covenants que matan", "Warehouse facility end-to-end", "Bridge / revolving", "SAFE-a-debt", "Mezzanine 101", "Ratchet & MFN", "Los 3 templates que uso"],
  },
  {
    n: "04", t_en: "Raise", t_es: "Raise",
    len_en: "1.5h · 7 lessons + data room", len_es: "1.5h · 7 lecciones + data room",
    d_en: "The data room that converts. Pitch scripts. Warm intros. What to do when you get ghosted.",
    d_es: "El data room que convierte. Scripts de pitch. Warm intros. Qué hacer cuando te ghostean.",
    lessons_en: ["Data room that converts", "Pitching a lender · script", "60-lender directory", "Warm intro scripts (email + LinkedIn)", "First-meeting cheatsheet", "Follow-up rhythm", "When you get ghosted"],
    lessons_es: ["Data room que convierte", "Pitch a un lender · script", "Directorio 60+ lenders", "Warm intro scripts", "Primer meeting cheatsheet", "Follow-up rhythm", "Cuando te ghostean"],
  },
  {
    n: "05", t_en: "Negotiate", t_es: "Negociar",
    len_en: "1.5h · 6 lessons + scripts", len_es: "1.5h · 6 lecciones + scripts",
    d_en: "The BATNA move. Rate vs covenants. The 10 clauses I always redline. When to walk away.",
    d_es: "El BATNA move. Rate vs covenants. Las 10 cláusulas que redlineo. Cuándo walk away.",
    lessons_en: ["BATNA: 2+ term sheets on the table", "Rate vs covenants trade", "MFN & ratchet negotiation", "Escrow / collateral talk", "10 clauses I always redline", "When to walk away"],
    lessons_es: ["BATNA: 2+ term sheets en mesa", "Rate vs covenants trade", "Negociar MFN & ratchet", "Escrow / collateral talk", "10 cláusulas que siempre redlineo", "Cuándo walk away"],
  },
  {
    n: "06", t_en: "Close", t_es: "Cerrar",
    len_en: "1h · 5 lessons + checklist", len_es: "1h · 5 lecciones + checklist",
    d_en: "Legal counsel picks. KYC/AML shortcuts. 30-item closing checklist. What happens week 1 post-close.",
    d_es: "Elegir abogado. KYC/AML rápido. Checklist de 30 ítems. Qué pasa semana 1 post-close.",
    lessons_en: ["Pick the right lawyer", "KYC/AML acceleration", "The 30+ item closing checklist", "First draw request", "Week 1 post-close"],
    lessons_es: ["Elegir el abogado correcto", "KYC/AML rápido", "Closing checklist · 30+ ítems", "First draw request", "Semana 1 post-close"],
  },
  {
    n: "07", t_en: "Scale", t_es: "Escalar",
    len_en: "1.5h · 6 lessons + refi model", len_es: "1.5h · 6 lecciones + refi model",
    d_en: "When to refinance. Second facility strategy. Layered credit. The waiver playbook.",
    d_es: "Cuándo refinanciar. Segunda facility. Crédito layered. Playbook de waivers.",
    lessons_en: ["When to refinance", "Second facility strategy", "Institutional graduation", "Layered credit · senior + mezz", "Reporting cadence", "The waiver playbook"],
    lessons_es: ["Cuándo refinanciar", "Segunda facility strategy", "Graduación institucional", "Crédito layered · senior + mezz", "Reporting cadence", "Playbook de waivers"],
  },
  {
    n: "08", t_en: "Case Studies · BONUS", t_es: "Case Studies · BONUS",
    len_en: "5 real term sheets · torn apart", len_es: "5 term sheets reales · desmenuzados",
    d_en: "Five real term sheets, redlined line by line. Fintech · SaaS · SMB · NBFI · Family office.",
    d_es: "Cinco term sheets reales, redlineados línea a línea. Fintech · SaaS · SMB · NBFI · Family office.",
    lessons_en: ["CS1 · Fintech consumer · $12M warehouse", "CS2 · SaaS lending · $25M line", "CS3 · SMB manufacturing · MXN 30M", "CS4 · NBFI Andean · $50M", "CS5 · Family office deployment"],
    lessons_es: ["CS1 · Fintech consumer · $12M warehouse", "CS2 · SaaS lending · $25M line", "CS3 · SMB manufacturing · MXN 30M", "CS4 · NBFI Andina · $50M", "CS5 · Family office deployment"],
  },
];

const TEMPLATE_CATEGORIES = [
  { icon: "▲", en: "Cap-table waterfall model", es: "Waterfall model del cap table" },
  { icon: "▲", en: "Cost-of-capital calculator", es: "Calculadora costo de capital" },
  { icon: "▲", en: "Lender readiness scorecard", es: "Lender readiness scorecard" },
  { icon: "◆", en: "Warehouse term sheet · v3.2", es: "Warehouse term sheet · v3.2" },
  { icon: "◆", en: "Bridge facility template", es: "Template facility bridge" },
  { icon: "◆", en: "Mezzanine term sheet", es: "Term sheet mezzanine" },
  { icon: "●", en: "Data room folder structure", es: "Estructura de data room" },
  { icon: "●", en: "Loan tape format v3.2", es: "Loan tape format v3.2" },
  { icon: "●", en: "KYC / AML checklist", es: "Checklist KYC / AML" },
  { icon: "■", en: "Warm intro email script", es: "Script warm intro email" },
  { icon: "■", en: "LinkedIn outreach template", es: "Template outreach LinkedIn" },
  { icon: "■", en: "Follow-up cadence · 12 touchpoints", es: "Cadence follow-up · 12 touchpoints" },
  { icon: "★", en: "60+ curated lender directory", es: "Directorio 60+ lenders" },
  { icon: "★", en: "Covenant taxonomy · annotated", es: "Taxonomía covenants · anotada" },
  { icon: "★", en: "Redline playbook · 10 clauses", es: "Playbook redline · 10 cláusulas" },
  { icon: "◇", en: "30-item closing checklist", es: "Checklist closing · 30 ítems" },
  { icon: "◇", en: "First draw request template", es: "Template first draw request" },
  { icon: "◇", en: "Refi decision framework", es: "Framework decisión refi" },
];


const FAQ = [
  {
    q_en: "When does the course open on Hotmart?",
    q_es: "¿Cuándo abre el curso en Hotmart?",
    a_en: "Cohort 01 opens November 2026. Get on the waitlist to unlock Module 1 immediately and a 20% early-bird discount.",
    a_es: "El primer batch abre noviembre 2026. Anótate en la lista para desbloquear el Módulo 1 al instante y un 20% early-bird.",
  },
  {
    q_en: "How is this different from your live 10-week Program?",
    q_es: "¿En qué se diferencia del Program live de 10 semanas?",
    a_en: "The Capital Playbook is self-paced — same method, no live sessions. The Program is $7.5k and adds warm intros to lenders, weekly office hours and a cohort. Both work; different speeds.",
    a_es: "The Capital Playbook es auto-gestionado — mismo método, sin sesiones en vivo. The Program cuesta $7.5k y añade warm intros a lenders, office hours semanales y cohorte. Ambos funcionan; diferentes ritmos.",
  },
  {
    q_en: "Is it bilingual for real, or dubbed?",
    q_es: "¿Es bilingüe real o doblado?",
    a_en: "Real. Every lesson is recorded twice — one native ES, one native EN — with matching subtitles. Ernesto teaches in your language.",
    a_es: "Real. Cada lección se grabó dos veces — una nativa ES, otra nativa EN — con subtítulos matching. Ernesto enseña en tu idioma.",
  },
  {
    q_en: "Money-back guarantee?",
    q_es: "¿Garantía money-back?",
    a_en: "14 days · no questions asked. Access the whole course, decide if it's worth it. Refund via Hotmart in a click.",
    a_es: "14 días · sin preguntas. Accede al curso completo, decide si vale. Reembolso vía Hotmart con un clic.",
  },
  {
    q_en: "Do I get lifetime access?",
    q_es: "¿Acceso de por vida?",
    a_en: "Yes. One-time payment · lifetime access to all future updates. When new case studies drop, you get them.",
    a_es: "Sí. Pago único · acceso de por vida + updates. Cuando salen case studies nuevos, los recibes.",
  },
  {
    q_en: "Can I expense this through my company?",
    q_es: "¿Puedo facturarlo a mi empresa?",
    a_en: "Yes. Hotmart issues fiscal invoices in MXN and USD. Add your RFC at checkout.",
    a_es: "Sí. Hotmart emite factura fiscal en MXN y USD. Agrega tu RFC al checkout.",
  },
];

export default function Playbook() {
  const [lang] = useState("es");
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [openMod, setOpenMod] = useState(0);
  const [openFaq, setOpenFaq] = useState(-1);
  const t = COPY[lang];

  const submit = async (e, src) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true); setErr("");
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email, source: src || "playbook_waitlist", lang });
      setDone(true);
    } catch { setErr("Try again."); } finally { setBusy(false); }
  };


  return (
    <div style={{ background: BG, color: CREAM, minHeight: "100vh", position: "relative", overflowX: "hidden" }} data-testid="playbook-root">
      <style>{`
        * { box-sizing: border-box; }
        html, body { background: ${BG}; scroll-behavior: smooth; scroll-padding-top: 90px; }
        body { font-family: 'Archivo', sans-serif; color: ${CREAM}; -webkit-font-smoothing: antialiased; }
        h1, h2, h3, .fraunces-hd, .pb-hero-title, .fraunces-italic { font-family: 'Archivo', sans-serif !important; font-style: normal !important; }
        .fraunces-hd, .pb-hero-title, .fraunces-italic, h1, h2 { font-weight: 900 !important; letter-spacing: -0.03em !important; }
        .tag { font-weight: 700 !important; }
        ::selection { background: ${LIME}; color: #141414; }
        .serif { font-family: 'Archivo', sans-serif; }
        .mono { font-family: 'Archivo', sans-serif; }
        .fraunces-italic { font-family: 'Archivo', sans-serif; font-style: normal; font-weight: 900; font-variation-settings: "SOFT" 100, "opsz" 144; }
        .fraunces-hd { font-family: 'Archivo', sans-serif; font-weight: 300; font-variation-settings: "SOFT" 30, "opsz" 144; letter-spacing: -0.045em; line-height: 0.95; }
        .grain::before { content: ""; position: fixed; inset: 0; z-index: 1; pointer-events: none; opacity: 0.04; background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E"); mix-blend-mode: multiply; }
        .btn-primary { display: inline-flex; align-items: center; gap: 8px; padding: 16px 28px; background: ${LIME}; color: #141414; border: 2px solid #141414; border-radius: 14px; box-shadow: 4px 4px 0 #141414; cursor: pointer; font-family: 'Archivo', sans-serif; font-size: 11px; letter-spacing: 0.06em; text-transform: uppercase; font-weight: 800; text-decoration: none; transition: transform 0.2s, box-shadow 0.2s; }
        .btn-primary:hover { transform: translate(2px,2px); box-shadow: 2px 2px 0 #141414; }
        .btn-ghost { display: inline-flex; align-items: center; gap: 8px; padding: 15px 22px; background: transparent; color: ${CREAM}; border: 2px solid #141414; border-radius: 14px; cursor: pointer; font-family: 'Archivo', sans-serif; font-size: 10.5px; letter-spacing: 0.1em; text-transform: uppercase; font-weight: 700; text-decoration: none; }
        .form-input { flex: 1; min-width: 200px; background: #FFFFFF; border: 2px solid #141414; border-radius: 10px; color: ${CREAM}; padding: 16px 18px; font-family: 'Archivo', sans-serif; font-size: 13px; outline: none; }
        .form-input:focus { border-color: ${GOLD}; }
        .pb-hero-title { font-size: clamp(56px, 9vw, 148px); }
        .pb-hero-grid { display: grid; grid-template-columns: 1.2fr 1fr; gap: 60px; align-items: end; }
        .pb-stat-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; padding: 40px 0; border-top: 1px solid ${GOLD}33; border-bottom: 1px solid ${GOLD}33; }
        .pb-outcomes-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
        .pb-templates-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
        .pb-instructor-grid { display: grid; grid-template-columns: 1fr 1.2fr; gap: 60px; align-items: center; }
        .mod-card { transition: all 0.3s ease; }
        .mod-card:hover { border-color: ${GOLD}77 !important; }
        @media (max-width: 900px) {
          .pb-hero-grid { grid-template-columns: 1fr; gap: 40px; }
          .pb-stat-grid { grid-template-columns: repeat(2, 1fr); gap: 30px; }
          .pb-outcomes-grid { grid-template-columns: 1fr; }
          .pb-templates-grid { grid-template-columns: repeat(2, 1fr); }
          .pb-instructor-grid { grid-template-columns: 1fr; }
          .pb-hero-title { font-size: clamp(44px, 12vw, 88px) !important; }
        }
        @media (max-width: 480px) {
          .pb-templates-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="grain" />

      {/* Promo bar */}
      <div style={{ background: BLUE, color: "#FFFFFF", borderRadius: 0, padding: "10px 20px", textAlign: "center", fontFamily: "'Archivo', sans-serif", fontSize: 10, letterSpacing: "0.28em", fontWeight: 700, position: "relative", zIndex: 20 }} data-testid="playbook-promo">
        {t.promo}
      </div>

      {/* Nav minimal */}
      <nav
        style={{ position: "sticky", top: 0, zIndex: 30, background: `${BG}dd`, backdropFilter: "blur(20px)", borderBottom: `1px solid ${CREAM}10`, padding: "16px 40px", display: "flex", justifyContent: "space-between", alignItems: "center" }}
        data-testid="playbook-nav"
      >
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: 12, textDecoration: "none", color: CREAM }}>
          <LogoMark size={30} />
          <div>
            <div className="mono" style={{ fontSize: 11, letterSpacing: "0.16em", fontWeight: 600, whiteSpace: "nowrap" }}>THE DEBT GUY</div>
            <div className="mono brand-sub" style={{ fontSize: 8.5, letterSpacing: "0.28em", opacity: 0.5, textTransform: "uppercase", marginTop: 2, whiteSpace: "nowrap" }}>Ernesto Gómez Berjón</div>
          </div>
        </Link>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <a href="#waitlist" className="btn-primary" style={{ padding: "12px 18px", fontSize: 10 }} data-testid="playbook-nav-cta">
            {t.ctaWaitlist.split(" ").slice(0, 3).join(" ")}
          </a>
        </div>
      </nav>


      {/* HERO */}
      <section style={{ padding: "80px 40px 60px", maxWidth: 1400, margin: "0 auto", position: "relative", zIndex: 5 }} data-testid="playbook-hero">
        <div className="mono" style={{ fontSize: 10.5, letterSpacing: "0.28em", color: GOLD, textTransform: "uppercase", marginBottom: 30 }}>
          {t.tag}
        </div>
        <div className="pb-hero-grid">
          <div>
            <h1 className="fraunces-hd pb-hero-title" data-testid="playbook-hero-title">
              {t.titleA} <span className="fraunces-italic" style={{ color: GOLD }}>{t.titleHL}</span>
            </h1>
            <p className="serif" style={{ marginTop: 22, fontSize: "clamp(20px, 2vw, 28px)", lineHeight: 1.25, letterSpacing: "-0.02em", opacity: 0.9, maxWidth: 720, margin: "22px 0 0", fontStyle: "normal", fontWeight: 300, fontVariationSettings: '"SOFT" 30, "opsz" 144' }}>
              {t.titleB}
            </p>
            <p style={{ marginTop: 22, fontSize: 16, lineHeight: 1.65, opacity: 0.75, maxWidth: 640 }}>
              {t.sub}
            </p>
          </div>

          {/* Price card */}
          <div style={{ background: BG_ALT, border: `1.5px solid ${GOLD}`, padding: 36, position: "relative" }} data-testid="playbook-price-card">
            <div style={{ position: "absolute", top: -12, left: 24, background: LIME, color: "#141414", borderRadius: 14, border: "2px solid #141414", boxShadow: "4px 4px 0 #141414", padding: "5px 12px", fontFamily: "'Archivo', sans-serif", fontSize: 9.5, letterSpacing: "0.22em", fontWeight: 700 }}>
              {t.priceLabel}
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 16 }}>
              <div className="fraunces-italic" style={{ fontSize: 84, color: GOLD, letterSpacing: "-0.03em", lineHeight: 1 }} data-testid="playbook-price">{t.price}</div>
            </div>
            <div className="mono" style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", opacity: 0.65, marginTop: 8 }}>{t.priceSub}</div>

            <div style={{ marginTop: 30, paddingTop: 24, borderTop: `1px solid ${CREAM}18` }}>
              {done ? (
                <div data-testid="playbook-hero-success" style={{ padding: 20, background: `${GOLD}15`, border: `1px solid ${GOLD}55` }}>
                  <div className="serif" style={{ fontSize: 18, fontStyle: "normal", color: GOLD, fontWeight: 300 }}>✓</div>
                  <div style={{ marginTop: 8, fontSize: 14, lineHeight: 1.5 }}>{t.successMsg}</div>
                </div>
              ) : (
                <form onSubmit={(e) => submit(e, "playbook_hero")} style={{ display: "flex", gap: 8, flexWrap: "wrap" }} data-testid="playbook-hero-form">
                  <input
                    type="email" required placeholder={t.email}
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="form-input" data-testid="playbook-hero-input"
                  />
                  <button type="submit" disabled={busy} className="btn-primary" data-testid="playbook-hero-submit">
                    {busy ? "..." : t.ctaWaitlist.split(" ")[0]}
                  </button>
                </form>
              )}
              {err && <div style={{ color: "#F97316", fontSize: 12, marginTop: 10 }}>{err}</div>}
              <div className="mono" style={{ marginTop: 14, fontSize: 9, letterSpacing: "0.24em", textTransform: "uppercase", opacity: 0.5 }}>
                {t.priceGuarantee}
              </div>
            </div>
          </div>
        </div>

        {/* Stat bar */}
        <div className="pb-stat-grid" style={{ marginTop: 70 }} data-testid="playbook-stats">
          <div><div className="fraunces-italic" style={{ fontSize: 68, color: GOLD, lineHeight: 1 }}>8</div><div className="mono" style={{ fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", opacity: 0.6, marginTop: 10 }}>{t.stat1}</div></div>
          <div><div className="fraunces-italic" style={{ fontSize: 68, color: GOLD, lineHeight: 1 }}>52</div><div className="mono" style={{ fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", opacity: 0.6, marginTop: 10 }}>{t.stat2}</div></div>
          <div><div className="fraunces-italic" style={{ fontSize: 68, color: GOLD, lineHeight: 1 }}>~12</div><div className="mono" style={{ fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", opacity: 0.6, marginTop: 10 }}>{t.stat3}</div></div>
          <div><div className="fraunces-italic" style={{ fontSize: 68, color: GOLD, lineHeight: 1 }}>30+</div><div className="mono" style={{ fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", opacity: 0.6, marginTop: 10 }}>{t.stat4}</div></div>
        </div>
      </section>

      {/* OUTCOMES */}
      <section style={{ padding: "100px 40px", maxWidth: 1400, margin: "0 auto", position: "relative", zIndex: 5 }} data-testid="playbook-outcomes">
        <div className="mono" style={{ fontSize: 10, letterSpacing: "0.28em", color: GOLD, textTransform: "uppercase", marginBottom: 22 }}>{t.outcomesTag}</div>
        <h2 className="fraunces-hd" style={{ fontSize: "clamp(34px, 4.5vw, 68px)", margin: 0 }}>
          {t.outcomesTitle} <span className="fraunces-italic" style={{ color: GOLD }}>{t.outcomesTitleHL}</span>
        </h2>
        <div className="pb-outcomes-grid" style={{ marginTop: 60 }}>
          {t.outcomes.map((o, i) => (
            <div key={i} data-testid={`playbook-outcome-${i}`} style={{ background: BG_ALT, border: `1px solid ${CREAM}12`, borderLeft: `2px solid ${GOLD}`, padding: 34 }}>
              <div className="fraunces-italic" style={{ fontSize: 52, color: GOLD, lineHeight: 1 }}>{o.n}</div>
              <div style={{ fontSize: 22, fontWeight: 500, marginTop: 14, lineHeight: 1.2, color: CREAM }}>{o.t}</div>
              <p style={{ marginTop: 12, fontSize: 15, lineHeight: 1.65, opacity: 0.75, margin: "12px 0 0" }}>{o.d}</p>
            </div>
          ))}
        </div>
      </section>


      {/* MODULES ACCORDION */}
      <section style={{ padding: "100px 40px", background: BG_ALT, position: "relative", zIndex: 5, borderTop: `1px solid ${CREAM}0f` }} data-testid="playbook-modules">
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div className="mono" style={{ fontSize: 10, letterSpacing: "0.28em", color: GOLD, textTransform: "uppercase", marginBottom: 22 }}>{t.modulesTag}</div>
          <h2 className="fraunces-hd" style={{ fontSize: "clamp(34px, 4.5vw, 68px)", margin: 0 }}>
            {t.modulesTitle} <span className="fraunces-italic" style={{ color: GOLD }}>{t.modulesTitleHL}</span>
          </h2>
          <div style={{ marginTop: 50 }}>
            {MODULES.map((m, i) => (
              <div key={i} data-testid={`playbook-module-${i}`} className="mod-card" style={{ borderTop: `1px solid ${CREAM}18`, borderBottom: i === MODULES.length - 1 ? `1px solid ${CREAM}18` : "none" }}>
                <button
                  type="button"
                  onClick={() => setOpenMod(openMod === i ? -1 : i)}
                  data-testid={`playbook-module-${i}-toggle`}
                  style={{ width: "100%", padding: "28px 0", background: "transparent", border: "none", cursor: "pointer", display: "grid", gridTemplateColumns: "auto 1fr auto auto", gap: 20, alignItems: "center", textAlign: "left", color: CREAM }}
                >
                  <div className="fraunces-italic" style={{ fontSize: 42, color: GOLD, lineHeight: 1, minWidth: 60 }}>{m.n}</div>
                  <div>
                    <div className="serif" style={{ fontSize: 26, fontWeight: 400, letterSpacing: "-0.02em", lineHeight: 1.15, fontVariationSettings: '"SOFT" 30, "opsz" 144' }}>
                      {lang === "en" ? m.t_en : m.t_es}
                      {m.free && <span className="mono" style={{ marginLeft: 14, fontSize: 9, letterSpacing: "0.24em", color: GOLD, background: `${GOLD}18`, padding: "3px 8px", verticalAlign: "middle" }}>{t.freeLabel}</span>}
                    </div>
                    <div className="mono" style={{ fontSize: 10.5, letterSpacing: "0.18em", textTransform: "uppercase", opacity: 0.55, marginTop: 6 }}>{lang === "en" ? m.len_en : m.len_es}</div>
                  </div>
                  <div style={{ display: "none" }} />
                  <div className="fraunces-italic" style={{ fontSize: 36, color: GOLD, fontWeight: 300, transition: "transform 0.3s ease", transform: openMod === i ? "rotate(45deg)" : "rotate(0deg)" }}>+</div>
                </button>
                <div style={{ maxHeight: openMod === i ? 800 : 0, overflow: "hidden", transition: "max-height 0.5s ease" }} data-testid={`playbook-module-${i}-body`}>
                  <div style={{ paddingBottom: 30, paddingLeft: 80 }}>
                    <p className="serif" style={{ fontSize: 18, lineHeight: 1.55, opacity: 0.82, fontStyle: "normal", maxWidth: 780, margin: 0 }}>
                      {lang === "en" ? m.d_en : m.d_es}
                    </p>
                    <div style={{ marginTop: 20, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
                      {(lang === "en" ? m.lessons_en : m.lessons_es).map((l, j) => (
                        <div key={j} style={{ fontSize: 13, lineHeight: 1.5, opacity: 0.78, display: "flex", gap: 12, alignItems: "flex-start" }}>
                          <span className="mono" style={{ color: GOLD, fontSize: 10, opacity: 0.85, minWidth: 26 }}>{m.n}.{String(j + 1).padStart(2, "0")}</span>
                          <span>{l}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TEMPLATES */}
      <section style={{ padding: "100px 40px", maxWidth: 1400, margin: "0 auto", position: "relative", zIndex: 5 }} data-testid="playbook-templates">
        <div className="mono" style={{ fontSize: 10, letterSpacing: "0.28em", color: GOLD, textTransform: "uppercase", marginBottom: 22 }}>{t.templatesTag}</div>
        <h2 className="fraunces-hd" style={{ fontSize: "clamp(34px, 4.5vw, 68px)", margin: 0 }}>
          {t.templatesTitle} <span className="fraunces-italic" style={{ color: GOLD }}>{t.templatesTitleHL}</span>
        </h2>
        <p className="serif" style={{ marginTop: 20, fontSize: 18, lineHeight: 1.55, opacity: 0.78, fontStyle: "normal", maxWidth: 720 }}>
          {t.templatesSub}
        </p>
        <div className="pb-templates-grid" style={{ marginTop: 50 }}>
          {TEMPLATE_CATEGORIES.map((tp, i) => (
            <div key={i} data-testid={`playbook-template-${i}`} style={{ background: BG_ALT, border: `1px solid ${CREAM}12`, padding: 22, display: "flex", alignItems: "center", gap: 16, transition: "border-color 0.2s" }} className="mod-card">
              <div style={{ fontSize: 22, color: GOLD, fontFamily: "'Archivo', sans-serif", flexShrink: 0, width: 30 }}>{tp.icon}</div>
              <div style={{ fontSize: 14, lineHeight: 1.4, color: CREAM }}>{lang === "en" ? tp.en : tp.es}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FREEMIUM Module 1 CTA */}
      <section style={{ padding: "100px 40px", background: `linear-gradient(135deg, ${GOLD}0d, transparent 60%)`, borderTop: `1px solid ${GOLD}22`, borderBottom: `1px solid ${GOLD}22`, position: "relative", zIndex: 5 }} data-testid="playbook-freemium">
        <div style={{ maxWidth: 900, margin: "0 auto", textAlign: "center" }}>
          <div className="mono" style={{ fontSize: 10, letterSpacing: "0.28em", color: GOLD, textTransform: "uppercase", marginBottom: 22 }}>— {t.freeLabel} · MÓDULO 01</div>
          <h2 className="fraunces-hd" style={{ fontSize: "clamp(34px, 5vw, 72px)", margin: 0 }}>
            {t.freemium1} <span className="fraunces-italic" style={{ color: GOLD }}>{t.freemium1HL}</span>
          </h2>
          <p className="serif" style={{ marginTop: 22, fontSize: 19, lineHeight: 1.55, opacity: 0.85, fontStyle: "normal", maxWidth: 640, margin: "22px auto 0" }}>
            {t.freemium1Sub}
          </p>
          {done ? (
            <div style={{ marginTop: 32, display: "inline-block", padding: "20px 30px", background: `${GOLD}22`, border: `1px solid ${GOLD}55` }} data-testid="playbook-freemium-success">
              <div className="mono" style={{ fontSize: 11, letterSpacing: "0.24em", color: GOLD }}>{t.successMsg}</div>
            </div>
          ) : (
            <form onSubmit={(e) => submit(e, "playbook_freemium")} style={{ marginTop: 34, display: "flex", gap: 10, maxWidth: 500, margin: "34px auto 0", flexWrap: "wrap", justifyContent: "center" }} data-testid="playbook-freemium-form">
              <input
                type="email" required placeholder={t.email}
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="form-input"
                style={{ minWidth: 260, borderColor: `${GOLD}` }}
                data-testid="playbook-freemium-input"
              />
              <button type="submit" disabled={busy} className="btn-primary" data-testid="playbook-freemium-submit">
                {busy ? "..." : t.ctaSecondary}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* BONUS STACK · $2,685 in bonuses included */}
      <BonusStack testid="playbook-bonus-stack" />

      {/* CERTIFICATE · shareable credential mockup */}
      <CertificateMockup testid="playbook-certificate" courseName="The Capital Playbook" studentName="Tu Nombre Aquí" />

      {/* PAYMENT METHODS · LATAM-friendly options */}
      <PaymentMethods testid="playbook-payments" />


      {/* INSTRUCTOR */}
      <section style={{ padding: "120px 40px", position: "relative", zIndex: 5 }} data-testid="playbook-instructor">
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div className="mono" style={{ fontSize: 10, letterSpacing: "0.28em", color: GOLD, textTransform: "uppercase", marginBottom: 22 }}>{t.instructorTag}</div>
          <div className="pb-instructor-grid">
            <div style={{
              aspectRatio: "3 / 4",
              backgroundImage: `url('https://customer-assets-7cd3h4nn.emergentagent.net/job_raise-like-insider/artifacts/49gzz0ou_31066C61-A699-4CE6-96B2-1FEA5AB108AD.png')`,
              backgroundSize: "cover", backgroundPosition: "center 15%",
              borderRadius: 18, border: "2px solid #141414", boxShadow: "8px 8px 0 #141414",
              position: "relative",
            }}>
              <div style={{ position: "absolute", bottom: 20, left: 20, right: 20, background: `${BG}dd`, padding: 18, backdropFilter: "blur(10px)" }}>
                <div className="serif" style={{ fontSize: 15, lineHeight: 1.4, fontStyle: "normal", color: CREAM }}>{t.quote}</div>
                <div className="mono" style={{ marginTop: 8, fontSize: 9, letterSpacing: "0.24em", textTransform: "uppercase", opacity: 0.6 }}>— E.G.B.</div>
              </div>
            </div>
            <div>
              <h2 className="fraunces-hd" style={{ fontSize: "clamp(34px, 4.5vw, 68px)", margin: 0 }}>
                {t.instructorTitle}
              </h2>
              <div className="mono" style={{ marginTop: 16, fontSize: 11, letterSpacing: "0.22em", color: GOLD, textTransform: "uppercase" }}>{t.instructorRole}</div>
              <p className="serif" style={{ marginTop: 24, fontSize: 20, lineHeight: 1.55, fontStyle: "normal", opacity: 0.85, fontWeight: 300, fontVariationSettings: '"SOFT" 30, "opsz" 144' }}>
                {t.instructorBio}
              </p>
              <div style={{ marginTop: 34, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24 }}>
                <div><div className="fraunces-italic" style={{ fontSize: 42, color: GOLD, lineHeight: 1 }}>$1.2B+</div><div className="mono" style={{ fontSize: 9, letterSpacing: "0.22em", opacity: 0.6, textTransform: "uppercase", marginTop: 8 }}>{lang === "en" ? "signed term sheets" : "term sheets firmados"}</div></div>
                <div><div className="fraunces-italic" style={{ fontSize: 42, color: GOLD, lineHeight: 1 }}>10+</div><div className="mono" style={{ fontSize: 9, letterSpacing: "0.22em", opacity: 0.6, textTransform: "uppercase", marginTop: 8 }}>{lang === "en" ? "years operating" : "años operando"}</div></div>
                <div><div className="fraunces-italic" style={{ fontSize: 42, color: GOLD, lineHeight: 1 }}>9</div><div className="mono" style={{ fontSize: 9, letterSpacing: "0.22em", opacity: 0.6, textTransform: "uppercase", marginTop: 8 }}>{lang === "en" ? "jurisdictions" : "jurisdicciones"}</div></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GUARANTEE + FAQ */}
      <section style={{ padding: "120px 40px", background: BG_ALT, position: "relative", zIndex: 5, borderTop: `1px solid ${CREAM}0f` }} data-testid="playbook-faq">
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 30 }}>
            <GuaranteeBadge title="14 días" subtitle={lang === "en" ? "money back" : "garantía"} caption={lang === "en" ? "On every product · No questions" : "Sin preguntas · en todo"} size={140} testid="playbook-guarantee" />
          </div>
          <div className="mono" style={{ fontSize: 10, letterSpacing: "0.28em", color: GOLD, textTransform: "uppercase", marginBottom: 22, textAlign: "center" }}>{t.faqTag}</div>
          <h2 className="fraunces-hd" style={{ fontSize: "clamp(30px, 4vw, 56px)", margin: 0, textAlign: "center" }}>
            {t.faqTitle}
          </h2>
          <div style={{ marginTop: 50 }}>
            {FAQ.map((f, i) => (
              <div key={i} data-testid={`playbook-faq-${i}`} style={{ borderTop: `1px solid ${CREAM}15`, borderBottom: i === FAQ.length - 1 ? `1px solid ${CREAM}15` : "none" }}>
                <button type="button" onClick={() => setOpenFaq(openFaq === i ? -1 : i)} data-testid={`playbook-faq-${i}-toggle`} style={{ width: "100%", padding: "22px 0", background: "transparent", border: "none", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", color: CREAM, textAlign: "left", fontFamily: "'Archivo', sans-serif" }}>
                  <span style={{ fontSize: 16, fontWeight: 500, lineHeight: 1.35, paddingRight: 20 }}>{lang === "en" ? f.q_en : f.q_es}</span>
                  <span className="fraunces-italic" style={{ fontSize: 28, color: GOLD, flexShrink: 0, transition: "transform 0.3s ease", transform: openFaq === i ? "rotate(45deg)" : "rotate(0)" }}>+</span>
                </button>
                <div style={{ maxHeight: openFaq === i ? 400 : 0, overflow: "hidden", transition: "max-height 0.4s ease, padding 0.3s ease", paddingBottom: openFaq === i ? 22 : 0 }} data-testid={`playbook-faq-${i}-body`}>
                  <p className="serif" style={{ fontSize: 16, lineHeight: 1.6, opacity: 0.82, fontStyle: "normal", margin: 0, maxWidth: 720 }}>
                    {lang === "en" ? f.a_en : f.a_es}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL WAITLIST CTA */}
      <section id="waitlist" style={{ padding: "120px 40px", position: "relative", zIndex: 5, borderTop: `1px solid ${GOLD}22`, background: `linear-gradient(135deg, ${GOLD}0d, transparent 60%)` }} data-testid="playbook-waitlist">
        <div style={{ maxWidth: 900, margin: "0 auto", textAlign: "center" }}>
          <div className="mono" style={{ fontSize: 10, letterSpacing: "0.28em", color: GOLD, textTransform: "uppercase", marginBottom: 22 }}>{t.finalTag}</div>
          <h2 className="fraunces-hd" style={{ fontSize: "clamp(38px, 5.5vw, 96px)", margin: 0 }}>
            {t.finalTitle} <br />
            <span className="fraunces-italic" style={{ color: GOLD }}>{t.finalTitleHL}</span>
          </h2>
          <p className="serif" style={{ marginTop: 22, fontSize: 19, lineHeight: 1.55, opacity: 0.85, fontStyle: "normal", maxWidth: 640, margin: "22px auto 0" }}>
            {t.finalSub}
          </p>
          {done ? (
            <div style={{ marginTop: 40, display: "inline-block", padding: "24px 40px", background: `${GOLD}22`, border: `1px solid ${GOLD}55` }} data-testid="playbook-waitlist-success">
              <div className="mono" style={{ fontSize: 12, letterSpacing: "0.22em", color: GOLD }}>{t.successMsg}</div>
            </div>
          ) : (
            <form onSubmit={(e) => submit(e, "playbook_final")} style={{ marginTop: 40, display: "flex", gap: 10, maxWidth: 540, margin: "40px auto 0", flexWrap: "wrap", justifyContent: "center" }} data-testid="playbook-waitlist-form">
              <input
                type="email" required placeholder={t.email}
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="form-input" style={{ minWidth: 260 }}
                data-testid="playbook-waitlist-input"
              />
              <button type="submit" disabled={busy} className="btn-primary" data-testid="playbook-waitlist-submit">
                {busy ? "..." : t.ctaWaitlist}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Footer minimal */}
      <footer style={{ padding: "40px 40px", borderTop: `1px solid ${CREAM}0f`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 20, flexWrap: "wrap", maxWidth: 1400, margin: "0 auto", position: "relative", zIndex: 5 }} data-testid="playbook-footer">
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <LogoMark size={26} />
          <p style={{ margin: 0, fontFamily: "'Archivo', sans-serif", fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", opacity: 0.5 }}>{t.footer}</p>
        </div>
        <Link to="/" data-testid="playbook-footer-back" style={{ fontFamily: "'Archivo', sans-serif", fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", color: CREAM, opacity: 0.55, textDecoration: "none", whiteSpace: "nowrap" }}>{t.back}</Link>
      </footer>

      <StickyCTABar
        title={t.finalTitleHL}
        subtitle={t.finalSub}
        ctaText={t.ctaWaitlist.split(" ").slice(0, 3).join(" ")}
        ctaHref="#waitlist"
        secondaryText={t.ctaSecondary}
        secondaryHref="#playbook-freemium"
        testid="playbook-sticky-cta"
      />
    </div>
  );
}

